import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import '../styles/Navbar.css';
import eduConnectLogo from '../assets/educonnect-logo.png';

const ICONS = {
  profile: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  groups: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  upload: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>),
  calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
  logout: (<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>),
  login: (<><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></>),
  register: (<><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></>),
  menu: (<><line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="17" x2="21" y2="17" /></>),
  close: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
};

const Icon = ({ name, className = '' }) => (
  <svg className={`nav-icon ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

function readUser() {
  const token = localStorage.getItem('token');
  if (!token) return { user: null, userInfo: null };
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const fullName = `${payload.firstName || ''} ${payload.lastName || ''}`.trim();
    return {
      user: { token },
      userInfo: {
        username: fullName || payload.username || (payload.email ? payload.email.split('@')[0] : 'Kullanıcı'),
        id: payload.userId ?? payload.id,
        email: payload.email,
      },
    };
  } catch (error) {
    console.error('Token decode hatası:', error);
    return { user: { token }, userInfo: { username: 'Kullanıcı' } };
  }
}

function Navbar() {
  const [{ user, userInfo }, setAuth] = useState(readUser);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  // Sayfa değiştiğinde oturum bilgisini yeniden oku (giriş/çıkış sonrası güncel kalsın)
  useEffect(() => {
    setAuth(readUser());
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Başka sekmede giriş/çıkış yapılırsa güncelle
  useEffect(() => {
    const onStorage = (e) => { if (e.key === 'token') setAuth(readUser()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Kaydırınca alt çizgi belirsin
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Mobil menü açıkken sayfa kaymasın, Esc ile kapansın
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    const onKey = (e) => { if (e.key === 'Escape') setIsMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setAuth({ user: null, userInfo: null });
    setIsMenuOpen(false);
    showNotification('Başarıyla çıkış yaptınız!', 'success');
    navigate('/');
  };

  const isActive = (path) =>
    location.pathname === path || (path === '/groups' && location.pathname.startsWith('/group/'));

  const navItems = [
    { path: '/profile', label: 'Profil', icon: 'profile' },
    { path: '/groups', label: 'Gruplar', icon: 'groups' },
    { path: '/chat', label: 'Sohbet', icon: 'chat' },
    { path: '/upload', label: 'Kaynak Havuzu', icon: 'upload' },
    { path: '/calendar', label: 'Etkinlik Takvimi', icon: 'calendar' },
  ];
  const initial = (userInfo?.username || '?').charAt(0).toUpperCase();

  return (
    <>
      <header className={`nb${scrolled ? ' is-scrolled' : ''}`}>
        <div className="nb-inner">
          <Link to="/" className="nb-brand" aria-label="EduConnect ana sayfa">
            <img src={eduConnectLogo} alt="" className="nb-logo" />
            <span className="nb-brand-text">EduConnect</span>
          </Link>

          {/* Masaüstü */}
          {user && (
            <nav className="nb-links" aria-label="Ana menü">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nb-link${isActive(item.path) ? ' is-active' : ''}`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}

          <div className="nb-actions">
            {user ? (
              <>
                <span className="nb-avatar" title={userInfo?.username}>
                  {initial}
                </span>
                <button type="button" className="nb-logout" onClick={handleLogout}>Çıkış yap</button>
              </>
            ) : (
              <>
                <Link to="/login" className="nb-login">Giriş yap</Link>
                <Link to="/register" className="nb-cta">Kayıt ol</Link>
              </>
            )}
          </div>

          {/* Mobil menü düğmesi */}
          <button
            type="button"
            className="nb-toggle"
            onClick={() => setIsMenuOpen((o) => !o)}
            aria-label={isMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
            aria-expanded={isMenuOpen}
            aria-controls="nb-panel"
          >
            <Icon name={isMenuOpen ? 'close' : 'menu'} />
          </button>
        </div>

        {/* Mobil panel */}
        <div id="nb-panel" className={`nb-panel${isMenuOpen ? ' is-open' : ''}`}>
          <div className="nb-panel-inner">
            {user && userInfo && (
              <div className="nb-user">
                <span className="nb-user-avatar">{initial}</span>
                <span className="nb-user-text">
                  <strong>{userInfo.username}</strong>
                  <small>Giriş yapıldı</small>
                </span>
              </div>
            )}

            <ul className="nb-panel-list">
              {user ? (
                <>
                  {navItems.map((item) => (
                    <li key={item.path}>
                      <Link to={item.path} className={`nb-panel-link${isActive(item.path) ? ' is-active' : ''}`}>
                        <Icon name={item.icon} />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button type="button" className="nb-panel-link is-danger" onClick={handleLogout}>
                      <Icon name="logout" />
                      <span>Çıkış yap</span>
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link to="/login" className={`nb-panel-link${isActive('/login') ? ' is-active' : ''}`}>
                      <Icon name="login" />
                      <span>Giriş yap</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/register" className="nb-panel-cta">
                      <Icon name="register" />
                      <span>Kayıt ol</span>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </header>

      {isMenuOpen && <div className="nb-overlay" onClick={() => setIsMenuOpen(false)} aria-hidden="true" />}
      <div className="nb-spacer" />
    </>
  );
}

export default Navbar;